import React, { type JSX } from 'react';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import styles from './BuildsGemPowerSection.module.scss';
import { useMemo } from 'react';
import LinkWrapper from '@resources/js/components/LinkWrapper/LinkWrapper';
import ExternalLinkIcon from '@resources/js/components/ExternalLinkIcon/ExternalLinkIcon';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';

interface BuildsGemPowerSectionViewModel {
  title: string;
  description: string;
  topPlayerData: BuildsGemPowerTopPlayerViewModel[];
  gemDisplayOrder: number[];
  gemLabels: Record<number, string>;
  noPowerLabel: string;
  dataNote: string;
}

interface BuildsGemPowerTopPlayerViewModel {
  link: string;
  characterName: string;
  scoreLabel: string;
  gemTraits: Record<number, BuildsGemPowerTopPlayerTraitEntry>;
}

interface BuildsGemPowerTopPlayerTraitEntry {
  statId: number;
  gemPowerBar: string;
  gemPowerLabel: string;
  traitMarkdown: string;
}

export default function BuildsGemPowerSection({
  title,
  description,
  topPlayerData,
  gemDisplayOrder,
  noPowerLabel,
  gemLabels,
  dataNote,
}: BuildsGemPowerSectionViewModel): JSX.Element | null {
  const gemsToShow = useMemo(() => {
    const gems = new Set();
    for (const player of topPlayerData) {
      Object.keys(player.gemTraits).forEach((key) => gems.add(Number(key)));
    }
    return gems;
  }, [topPlayerData]);

  if (topPlayerData.length === 0) {
    return (
      <VerticalContent>
        <EnhancedMarkdown
          markdown={`# ${title}`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <BuildsNoDataPlaceholder />
      </VerticalContent>
    );
  }

  return (
    <VerticalContent gap={'small'}>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      <div className={styles.BuildsGemPowerSectionDataNote}>{dataNote}</div>
      <div className={styles.BuildsGemPowerSectionTopPlayerContainer}>
        {topPlayerData.map((player) => (
          <div
            key={player.link}
            className={styles.BuildsGemPowerSectionTopPlayerBox}
          >
            <div className={styles.BuildsGemPowerSectionPlayerNameContainer}>
              <LinkWrapper
                href={player.link}
                target='_blank'
                className={styles.BuildsGemPowerSectionPlayerNameLink}
              >
                <EnhancedMarkdown
                  markdown={player.characterName}
                  overrides={buildsEnhancedMarkdownOverrides}
                />
                <ExternalLinkIcon />
              </LinkWrapper>
              <span className={styles.BuildsGemPowerSectionScoreLabel}>
                {player.scoreLabel}
              </span>
            </div>
            <table className={styles.BuildsGemPowerSectionTraitTable}>
              <tbody>
                {gemDisplayOrder
                  .filter((statId) => gemsToShow.has(Number(statId)))
                  .map((statId) => (
                    <GemPowerRow
                      key={statId}
                      statId={Number(statId)}
                      entry={player.gemTraits[statId]}
                      gemLabels={gemLabels}
                      noPowerLabel={noPowerLabel}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </VerticalContent>
  );
}

function GemPowerRow({
  statId,
  entry,
  gemLabels,
  noPowerLabel,
}: {
  statId: number;
  entry: BuildsGemPowerTopPlayerTraitEntry | undefined;
} & Pick<
  BuildsGemPowerSectionViewModel,
  'gemLabels' | 'noPowerLabel'
>): JSX.Element {
  if (!entry) {
    return (
      <tr className={styles.BuildsGemPowerSectionTraitTable__NoPower}>
        <td>
          <EnhancedMarkdown
            markdown={gemLabels[statId]}
            overrides={buildsEnhancedMarkdownOverrides}
          />
        </td>
        <td></td>
        <td></td>
        <td className={styles.BuildsGemPowerSectionTraitTable__Col3}>
          <em>{noPowerLabel}</em>
        </td>
      </tr>
    );
  }

  const columns = [
    gemLabels[statId],
    entry.gemPowerBar,
    entry.traitMarkdown,
    entry.gemPowerLabel,
  ];

  return (
    <tr key={statId}>
      {columns.map((markdown, columnIndex) => (
        <td
          key={columnIndex}
          className={
            styles[`BuildsGemPowerSectionTraitTable__Col${columnIndex}`]
          }
        >
          <EnhancedMarkdown
            markdown={markdown}
            overrides={buildsEnhancedMarkdownOverrides}
          />
        </td>
      ))}
    </tr>
  );
}
